import { Component, EventEmitter, Input, OnInit, Output, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { ACCOUNT_NAME_MAX, SHOP_NAME_MIN } from 'src/app/shared/account/account.validators';
import { ICreateAccount } from 'src/app/shared/account/create-account.helper';
import { urlValidator } from 'src/app/shared/validation/validation.helpers';
import { BaseStepComponent } from '../base-step.component';
import { ImageInputEvent } from 'src/app/shared/components/image-input/image-input.types';
import { getLogoGroup } from 'src/app/shared/components/image-input/image-input.utils';
import { Account } from 'src/app/shared/account/account.types';

@Component({
  selector: 'app-step2',
  templateUrl: './step2.component.html',
})
export class Step2Component extends BaseStepComponent implements OnInit, OnDestroy {
  @Input()
  public defaultValues: ICreateAccount;

  @Input()
  public backendValidationErrorsList: string[];

  @Output()
  public nextStep: EventEmitter<void> = new EventEmitter<void>();

  @Output()
  public prevStep: EventEmitter<void> = new EventEmitter<void>();

  @Output()
  public updateParentModel = new EventEmitter<{
    part: Partial<ICreateAccount>;
    isFormValid: boolean;
  }>();

  public form: FormGroup;
  public ACCOUNT_NAME_MAX = ACCOUNT_NAME_MAX;
  public readonly SHOP_NAME_MIN = SHOP_NAME_MIN;

  private unsubscribe: Subscription[] = [];

  private fb: FormBuilder;

  constructor(
    fb: FormBuilder,
  ) {
    super();
    this.fb = fb;
  }

  public ngOnInit(): void {
    this.initForm();
    this.updateParentModel.emit({ part: {}, isFormValid: this.checkForm() });
  }

  public initForm(): void {
    this.form = this.fb.group({
      name: [this.defaultValues.name, [
        Validators.required,
        Validators.minLength(this.SHOP_NAME_MIN),
        Validators.maxLength(ACCOUNT_NAME_MAX),
        (control: any): any => {
          const hasForbiddenCharacter = control.value && control.value.includes('_');
          return hasForbiddenCharacter ? { forbiddenCharacter: true } : null;
        }
      ]],
      ...(this.isShop()
        ? {
          domain: [this.defaultValues.domain,
            [
              Validators.required,
              Validators.maxLength(100),
              urlValidator(['http://', 'https://']),
            ]
          ],
          logo: getLogoGroup(this.fb, this.defaultValues?.logo, this.getFieldValue.bind(this)),
        }
        : {
          currency: [{ value: this.defaultValues.currency, disabled: true }, [Validators.required]],
        }
      )
    });

    const formChangesSubscription = this.form.valueChanges.subscribe(val => {
      this.updateParentModel.emit({ part: val, isFormValid: this.checkForm() });
    });
    this.unsubscribe.push(formChangesSubscription);
  }

  public isShop(): boolean {
    return this.defaultValues.accountType === Account.Type.PUBLISHER;
  }

  public checkForm(): boolean {
    return !this.isAnyHaveError(
      this.form,
      Object.keys(this.form.controls),
      ['required', 'minlength', 'maxlength']
    );
  }

  public onLogoChanged(data: ImageInputEvent): void {
    this.form.get('logo')?.setValue(data);
  }

  public ngOnDestroy(): void {
    this.unsubscribe.forEach(sb => sb.unsubscribe());
  }

  private getFieldValue(name: string) {
    return this.form.get(name)?.value;
  }
}
